/**
 * SketchBrowserComponent - Tree view for sketches and entities
 * browser panel
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UnitConversionService, UnitPipe, UnitLabelPipe } from '@infinitform/shared-ui';

import { SketchService } from '../../services/sketch.service';
import { 
  Sketch, 
  SketchEntity, 
  SketchProfile,
  TreeNode 
} from '../../types';

import { TooltipDirective } from '../../../ui/tooltip';

@Component({
    selector: 'sketch-browser',
    imports: [CommonModule, FormsModule, TooltipDirective, UnitPipe],
    templateUrl: './sketch-browser.component.html',
    styleUrls: ['./sketch-browser.component.scss']
})
export class SketchBrowserComponent implements OnInit, OnDestroy {
  private sketchService = inject(SketchService);
  private cdr = inject(ChangeDetectorRef);
  private unitConversionService = inject(UnitConversionService);
  unitSystem = this.unitConversionService.unitSystem;
  private subscriptions: Subscription[] = [];

  // Tree data
  treeNodes: TreeNode[] = [];
  
  // Preserve expanded state across refreshes
  private expandedNodeIds: Set<string> = new Set();
  
  // Editing state
  editingName = '';
  
  // Hover tracking
  hoveredNodeId: string | null = null;
  
  // Context menu state
  contextMenuVisible = false;
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuNode: TreeNode | null = null;
  
  // Events
  @Input() isSketchMode = false;
  @Input() selectedProfileIds: Set<string> = new Set();
  @Output() onSketchSelect = new EventEmitter<string>();
  @Output() onEntitySelect = new EventEmitter<{ sketchId: string; entityId: string }>();
  @Output() onProfileSelect = new EventEmitter<{ sketchId: string; profileId: string }>();
  @Output() onCreate = new EventEmitter<void>();
  @Output() onEdit = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onEntityDelete = new EventEmitter<{ sketchId: string; entityId: string }>();
  @Output() onNodeHovered = new EventEmitter<{ type: 'sketch' | 'entity' | 'profile' | 'folder'; id: string; parentId?: string } | null>();
  @Output() onVisibilityToggle = new EventEmitter<{ sketchId: string; visible: boolean }>();
  @Output() onFinishEditing = new EventEmitter<void>();

  ngOnInit(): void {
    this.refreshTree();
    
    // Subscribe to sketch service events
    this.subscriptions.push(
      this.sketchService.onSketchCreated.subscribe(() => this.refreshTree()),
      this.sketchService.onSketchUpdated.subscribe(() => this.refreshTree()),
      this.sketchService.onSketchDeleted.subscribe(() => this.refreshTree()),
      this.sketchService.onEntityCreated.subscribe(() => this.refreshTree()),
      this.sketchService.onEntityUpdated.subscribe(() => this.refreshTree()),
      this.sketchService.onEntityDeleted.subscribe(() => this.refreshTree()),
      this.sketchService.onProfilesUpdated.subscribe(() => this.refreshTree()),
      this.sketchService.onActiveSketchChanged.subscribe(() => this.refreshTree()),
      this.sketchService.onSelectionChanged.subscribe(() => this.refreshTree())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Refresh tree view from service while preserving expanded state
   */
  refreshTree(): void {
    // Save current expanded states
    this.saveExpandedStates(this.treeNodes);
    
    // Get new tree
    this.treeNodes = this.sketchService.buildTreeView();
    
    // Restore expanded states
    this.restoreExpandedStates(this.treeNodes);
    
    this.cdr.detectChanges();
  }

  /**
   * Save expanded states recursively
   */
  private saveExpandedStates(nodes: TreeNode[]): void {
    for (const node of nodes) {
      if (node.isExpanded) {
        this.expandedNodeIds.add(node.id);
      } else {
        this.expandedNodeIds.delete(node.id);
      }
      if (node.children.length > 0) {
        this.saveExpandedStates(node.children);
      }
    }
  }

  /**
   * Restore expanded states recursively
   */
  private restoreExpandedStates(nodes: TreeNode[]): void {
    for (const node of nodes) {
      if (this.expandedNodeIds.has(node.id)) {
        node.isExpanded = true;
      }
      if (node.children.length > 0) {
        this.restoreExpandedStates(node.children);
      }
    }
  }

  /**
   * Check if node represents the active sketch or selected profile
   */
  isNodeActive(node: TreeNode): boolean {
    if (node.type === 'sketch') {
      const activeSketch = this.sketchService.getActiveSketch();
      return activeSketch?.id === node.dataId;
    }
    if (node.type === 'profile') {
      return this.selectedProfileIds.has(node.dataId);
    }
    return false;
  }

  /**
   * Handle node click
   */
  onNodeClick(node: TreeNode, event: MouseEvent): void {
    event.stopPropagation();
    
    const ctrlKey = event.ctrlKey || event.metaKey;
    
    switch (node.type) {
      case 'sketch':
        this.onSketchSelect.emit(node.dataId);
        break;
      case 'entity':
        const parentSketch = this.findParentSketch(node);
        if (parentSketch) {
          this.sketchService.selectEntity(node.dataId, ctrlKey);
          this.onEntitySelect.emit({ sketchId: parentSketch.dataId, entityId: node.dataId });
        }
        break;
      case 'profile':
        const sketchForProfile = this.findParentSketch(node);
        if (sketchForProfile) {
          this.sketchService.selectProfile(node.dataId, ctrlKey);
          this.onProfileSelect.emit({ sketchId: sketchForProfile.dataId, profileId: node.dataId });
        }
        break;
    }
    
    // Note: refreshTree() is called automatically via service event subscriptions
    // Don't call it here to avoid conflicts with double-click editing
  }

  /**
   * Handle node double-click (edit sketch or start rename)
   */
  onNodeDoubleClick(node: TreeNode): void {
    if (node.type === 'sketch') {
      this.sketchService.setActiveSketch(node.dataId);
      this.onEdit.emit(node.dataId);
    } else if (node.type === 'entity' || node.type === 'profile') {
      // Start editing name
      node.isEditing = true;
      this.editingName = node.name;
      this.cdr.detectChanges();
      
      // Focus the input after it renders
      setTimeout(() => {
        const input = document.querySelector('.node-name-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 0);
    }
  }

  /**
   * Handle node context menu
   */
  onNodeContextMenu(node: TreeNode, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    this.contextMenuNode = node;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.contextMenuVisible = true;
    
    this.cdr.detectChanges();
  }

  /**
   * Close context menu
   */
  closeContextMenu(): void {
    this.contextMenuVisible = false;
    this.contextMenuNode = null;
    this.cdr.detectChanges();
  }

  /**
   * Context menu: Rename action
   */
  contextMenuRename(): void {
    if (this.contextMenuNode) {
      this.contextMenuNode.isEditing = true;
      this.editingName = this.contextMenuNode.name;
    }
    this.closeContextMenu();
  }

  /**
   * Context menu: Delete action
   */
  contextMenuDelete(): void {
    if (!this.contextMenuNode) {
      this.closeContextMenu();
      return;
    }
    
    const node = this.contextMenuNode;
    
    if (node.type === 'sketch') {
      this.onDelete.emit(node.dataId);
    } else if (node.type === 'entity') {
      const parentSketch = this.findParentSketch(node);
      if (parentSketch) {
        this.onEntityDelete.emit({ sketchId: parentSketch.dataId, entityId: node.dataId });
      }
    }
    
    this.closeContextMenu();
  }

  /**
   * Context menu: Duplicate action (sketches only)
   */
  contextMenuDuplicate(): void {
    if (this.contextMenuNode && this.contextMenuNode.type === 'sketch') {
      this.sketchService.duplicateSketch(this.contextMenuNode.dataId);
    }
    this.closeContextMenu();
  }

  /**
   * Context menu: Toggle construction (entities only)
   */
  contextMenuToggleConstruction(): void {
    if (this.contextMenuNode && this.contextMenuNode.type === 'entity') {
      const parentSketch = this.findParentSketch(this.contextMenuNode);
      if (parentSketch) {
        const activeSketch = this.sketchService.getActiveSketch();
        const entity = activeSketch?.entities.find(e => e.id === this.contextMenuNode!.dataId);
        if (entity) {
          this.sketchService.updateEntity(this.contextMenuNode.dataId, { 
            isConstruction: !entity.isConstruction 
          });
        }
      }
    }
    this.closeContextMenu();
  }

  /**
   * Toggle node expansion
   */
  toggleExpand(node: TreeNode, event: MouseEvent): void {
    event.stopPropagation();
    node.isExpanded = !node.isExpanded;
    
    // Save the expanded state
    if (node.isExpanded) {
      this.expandedNodeIds.add(node.id);
    } else {
      this.expandedNodeIds.delete(node.id);
    }
    
    this.cdr.detectChanges();
  }
  
  /**
   * Handle node hover - emit event for scene highlighting
   */
  onNodeHover(node: TreeNode | null): void {
    if (!node) {
      this.hoveredNodeId = null;
      this.onNodeHovered.emit(null);
      return;
    }
    
    this.hoveredNodeId = node.id;
    
    // Find parent sketch for entity/profile nodes
    const parentSketch = this.findParentSketch(node);
    
    this.onNodeHovered.emit({
      type: node.type as 'sketch' | 'entity' | 'profile' | 'folder',
      id: node.dataId,
      parentId: parentSketch?.dataId
    });
  }

  /**
   * Finish editing node name
   */
  finishEditing(node: TreeNode): void {
    node.isEditing = false;
    
    if (this.editingName.trim() && this.editingName !== node.name) {
      const parentSketch = this.findParentSketch(node);
      
      if (node.type === 'sketch') {
        this.sketchService.renameSketch(node.dataId, this.editingName.trim());
      } else if (node.type === 'entity' && parentSketch) {
        this.sketchService.renameEntity(parentSketch.dataId, node.dataId, this.editingName.trim());
      } else if (node.type === 'profile' && parentSketch) {
        this.sketchService.renameProfile(parentSketch.dataId, node.dataId, this.editingName.trim());
      }
    }
    
    this.refreshTree();
  }

  /**
   * Cancel editing
   */
  cancelEditing(node: TreeNode): void {
    node.isEditing = false;
    this.editingName = '';
    this.cdr.detectChanges();
  }

  /**
   * Find parent sketch node (handles nested folders)
   */
  private findParentSketch(node: TreeNode): TreeNode | null {
    for (const sketchNode of this.treeNodes) {
      // Direct child
      if (sketchNode.children.some(c => c.id === node.id)) {
        return sketchNode;
      }
      // Child in folder
      for (const folder of sketchNode.children) {
        if (folder.type === 'folder' && folder.children.some(c => c.id === node.id)) {
          return sketchNode;
        }
      }
    }
    return null;
  }

  /**
   * Handle node deletion
   */
  onDeleteNode(node: TreeNode, event: MouseEvent): void {
    event.stopPropagation();
    
    if (node.type === 'sketch') {
      this.onDelete.emit(node.dataId);
    } else if (node.type === 'entity') {
      const parentSketch = this.findParentSketch(node);
      if (parentSketch) {
        this.onEntityDelete.emit({ sketchId: parentSketch.dataId, entityId: node.dataId });
      }
    }
  }

  /**
   * Toggle sketch visibility
   */
  toggleVisibility(node: TreeNode, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    
    if (node.type === 'sketch') {
      const newVisibility = !node.isVisible;
      this.sketchService.setSketchVisibility(node.dataId, newVisibility);
      this.onVisibilityToggle.emit({ sketchId: node.dataId, visible: newVisibility });
    }
  }

  /**
   * Delete selected entities
   */
  deleteSelectedEntities(): void {
    const activeSketch = this.sketchService.getActiveSketch();
    if (!activeSketch) return;
    
    const selectedIds = this.sketchService.getSelectedEntityIds();
    for (const entityId of selectedIds) {
      this.sketchService.deleteEntity(entityId);
    }
  }

  /**
   * Create new sketch button handler
   */
  onCreateSketch(): void {
    this.onCreate.emit();
  }
}

